/**
 * Where a comment pinned to the floor plan sits — and how it follows the thing
 * it is about.
 *
 * Asked for on 2026-09-11 ("option 6"): comments pinned to a spot on the plan
 * or the 3D model — "check this radiator position" — between surveyor,
 * designer and installer. A comment is stored by the API (migration 0043);
 * this module is the pure geometry the portal, the installer pack and the plan
 * exports share, so a pin numbered 3 on screen is at the same place on paper.
 *
 * A PIN is always a plan point (`x`, `y` in plan metres on the scan's own
 * frame — world X and world Z, see `scan-plan.ts`) on one storey, optionally
 * with a height (world Y) when it was put on the 3D model.
 *
 * ATTACHED PINS FOLLOW. A pin may also name an element — a wall, a window /
 * door / opening, a room (by its floor's element id, the id the plan and the
 * 3D view select a room by) or a plan object — together with where it sat
 * relative to that element when it was dropped (`anchor`). Drawn later, it is
 * put back at the same place relative to the element as it is NOW: a wall
 * corrected on the plan (0039) or a radiator dragged along it takes its
 * comments with it. Two frames cover every element:
 *
 *   segment  a wall, an opening, or one leg of a pipe run: a fraction `t`
 *            along it from its start, and `offsetM` off it to its left
 *            (plan left of start -> end). A wall that grows keeps the pin at
 *            the same fraction, which is what "the middle of this wall" means.
 *   local    a room (about its centre) or a placed object (about its centre,
 *            turned with it): `u`, `v` metres in that frame.
 *
 * DETACHED, NEVER LOST. An element that has gone — a wall deleted on the
 * plan, a radiator removed — leaves its comments where they were dropped, and
 * says so (`state: "detached"`), rather than hiding a conversation.
 */

import type { PlanPoint, PlanRoom, PlanSegment } from "./scan-plan.js";

export const COMMENT_TARGET_KINDS = ["wall", "opening", "room", "object"] as const;
export type CommentTargetKind = (typeof COMMENT_TARGET_KINDS)[number];

export interface CommentTarget {
  readonly kind: CommentTargetKind;
  readonly id: string;
}

export type CommentAnchor =
  | {
      readonly frame: "segment";
      readonly t: number;
      readonly offsetM: number;
      /** Which leg of a pipe run; absent for a wall or opening. */
      readonly leg?: number;
    }
  | { readonly frame: "local"; readonly u: number; readonly v: number };

/** A comment's pin as stored. */
export interface CommentPin {
  readonly storey: number;
  /** Where it was dropped, plan metres. */
  readonly x: number;
  readonly y: number;
  /** World Y, metres, when it was dropped on the 3D model. */
  readonly heightM?: number | null;
  readonly target?: CommentTarget | null;
  readonly anchor?: CommentAnchor | null;
}

/**
 * What the pin needs to know about a plan object: its id, storey and
 * geometry, and — for a placed one — its footprint, for "is this click on
 * it". Structural, so the portal's `PlanObject` and the API's view both fit.
 */
export interface CommentObjectLike {
  readonly id: string;
  readonly storey: number;
  readonly geometry:
    | { readonly x: number; readonly y: number; readonly rotationDeg: number }
    | { readonly points: readonly PlanPoint[] };
  /** Width along the object's own x and depth along its y, metres. */
  readonly footprintM?: { readonly w: number; readonly d: number };
}

export type CommentPinState = "free" | "attached" | "detached";

export interface ResolvedCommentPin {
  readonly point: PlanPoint;
  readonly storey: number;
  /** Free: not attached. Attached: drawn relative to its element. Detached: its element has gone. */
  readonly state: CommentPinState;
}

const OPENING_KINDS: ReadonlySet<string> = new Set(["window", "door", "opening"]);

function isRun(
  geometry: CommentObjectLike["geometry"],
): geometry is { readonly points: readonly PlanPoint[] } {
  return "points" in geometry;
}

function segmentFrame(start: PlanPoint, end: PlanPoint, point: PlanPoint): { t: number; offsetM: number; distanceM: number } {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) {
    return { t: 0, offsetM: 0, distanceM: Math.hypot(point.x - start.x, point.y - start.y) };
  }
  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (length * length);
  // Left of start -> end: the normal (-dy, dx).
  const offsetM = ((point.x - start.x) * -dy + (point.y - start.y) * dx) / length;
  const clamped = Math.max(0, Math.min(1, t));
  const cx = start.x + dx * clamped;
  const cy = start.y + dy * clamped;
  return { t, offsetM, distanceM: Math.hypot(point.x - cx, point.y - cy) };
}

function onSegment(start: PlanPoint, end: PlanPoint, t: number, offsetM: number): PlanPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  const nx = length < 1e-9 ? 0 : -dy / length;
  const ny = length < 1e-9 ? 0 : dx / length;
  return { x: start.x + dx * t + nx * offsetM, y: start.y + dy * t + ny * offsetM };
}

function toLocal(centre: PlanPoint, rotationDeg: number, point: PlanPoint): { u: number; v: number } {
  const r = (rotationDeg * Math.PI) / 180;
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  return { u: dx * Math.cos(r) + dy * Math.sin(r), v: -dx * Math.sin(r) + dy * Math.cos(r) };
}

function fromLocal(centre: PlanPoint, rotationDeg: number, u: number, v: number): PlanPoint {
  const r = (rotationDeg * Math.PI) / 180;
  return { x: centre.x + u * Math.cos(r) - v * Math.sin(r), y: centre.y + u * Math.sin(r) + v * Math.cos(r) };
}

function pointInPolygon(p: PlanPoint, polygon: readonly PlanPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i] as PlanPoint;
    const b = polygon[j] as PlanPoint;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function findSegment(rooms: readonly PlanRoom[], id: string): PlanSegment | undefined {
  for (const room of rooms) {
    const found = room.segments.find((s) => s.id === id);
    if (found) return found;
  }
  return undefined;
}

function findRoomByFloor(rooms: readonly PlanRoom[], floorId: string): PlanRoom | undefined {
  return rooms.find((room) => room.floorIds.includes(floorId));
}

/** The nearest leg of a run to `point`: its index and where on it. */
function nearestLeg(points: readonly PlanPoint[], point: PlanPoint): { leg: number; t: number; offsetM: number; distanceM: number } | undefined {
  let best: { leg: number; t: number; offsetM: number; distanceM: number } | undefined;
  for (let i = 1; i < points.length; i += 1) {
    const frame = segmentFrame(points[i - 1] as PlanPoint, points[i] as PlanPoint, point);
    if (!best || frame.distanceM < best.distanceM) best = { leg: i - 1, ...frame };
  }
  return best;
}

/**
 * What a comment dropped at `point` is about, if anything: an object under it
 * (they sit on top), else a window, door or opening within `radiusM`, else a
 * wall within `radiusM`, else the room whose floor it is on. Pass one storey's
 * rooms and objects. Null on open ground outside every room.
 */
export function commentTargetAt(
  point: PlanPoint,
  rooms: readonly PlanRoom[],
  objects: readonly CommentObjectLike[],
  radiusM: number,
): CommentTarget | null {
  // Objects: the nearest one the point is on (a placed object's footprint,
  // grown by the radius; a pipe run within the radius of its line).
  let bestObject: { id: string; distanceM: number } | undefined;
  for (const object of objects) {
    const g = object.geometry;
    let distanceM: number | undefined;
    if (isRun(g)) {
      const leg = nearestLeg(g.points, point);
      if (leg && leg.distanceM <= radiusM) distanceM = leg.distanceM;
    } else {
      const { u, v } = toLocal({ x: g.x, y: g.y }, g.rotationDeg, point);
      const halfW = (object.footprintM?.w ?? 0) / 2 + radiusM;
      const halfD = (object.footprintM?.d ?? 0) / 2 + radiusM;
      if (Math.abs(u) <= halfW && Math.abs(v) <= halfD) distanceM = Math.hypot(u, v);
    }
    if (distanceM !== undefined && (!bestObject || distanceM < bestObject.distanceM)) {
      bestObject = { id: object.id, distanceM };
    }
  }
  if (bestObject) return { kind: "object", id: bestObject.id };

  const nearest = (openings: boolean): PlanSegment | undefined => {
    let best: { segment: PlanSegment; distanceM: number } | undefined;
    for (const room of rooms) {
      for (const segment of room.segments) {
        if (OPENING_KINDS.has(segment.kind) !== openings) continue;
        if (!openings && segment.kind !== "wall") continue;
        const { distanceM } = segmentFrame(segment.start, segment.end, point);
        if (distanceM <= radiusM && (!best || distanceM < best.distanceM)) best = { segment, distanceM };
      }
    }
    return best?.segment;
  };
  const opening = nearest(true);
  if (opening) return { kind: "opening", id: opening.id };
  const wall = nearest(false);
  if (wall) return { kind: "wall", id: wall.id };

  for (const room of rooms) {
    for (let i = 0; i < room.outlines.length; i += 1) {
      const outline = room.outlines[i] as readonly PlanPoint[];
      const floorId = room.floorIds[i];
      if (floorId && pointInPolygon(point, outline)) return { kind: "room", id: floorId };
    }
  }
  return null;
}

/**
 * Where `point` sits relative to `target` as the plan stands now — what is
 * stored beside the target so the pin can follow it. Null when the target is
 * not on the plan (then store the pin free).
 */
export function commentAnchorFor(
  point: PlanPoint,
  target: CommentTarget,
  rooms: readonly PlanRoom[],
  objects: readonly CommentObjectLike[],
): CommentAnchor | null {
  switch (target.kind) {
    case "wall":
    case "opening": {
      const segment = findSegment(rooms, target.id);
      if (!segment) return null;
      const { t, offsetM } = segmentFrame(segment.start, segment.end, point);
      return { frame: "segment", t, offsetM };
    }
    case "room": {
      const room = findRoomByFloor(rooms, target.id);
      if (!room) return null;
      return { frame: "local", u: point.x - room.centre.x, v: point.y - room.centre.y };
    }
    case "object": {
      const object = objects.find((o) => o.id === target.id);
      if (!object) return null;
      const g = object.geometry;
      if (isRun(g)) {
        const leg = nearestLeg(g.points, point);
        return leg ? { frame: "segment", t: leg.t, offsetM: leg.offsetM, leg: leg.leg } : null;
      }
      const { u, v } = toLocal({ x: g.x, y: g.y }, g.rotationDeg, point);
      return { frame: "local", u, v };
    }
  }
}

/**
 * Where a pin is drawn now. An attached pin whose element is still on the
 * plan is put back relative to it; one whose element has gone stays where it
 * was dropped, marked detached.
 */
export function resolveCommentPin(
  pin: CommentPin,
  rooms: readonly PlanRoom[],
  objects: readonly CommentObjectLike[],
): ResolvedCommentPin {
  const dropped: ResolvedCommentPin = { point: { x: pin.x, y: pin.y }, storey: pin.storey, state: "free" };
  const { target, anchor } = pin;
  if (!target) return dropped;
  const detached: ResolvedCommentPin = { ...dropped, state: "detached" };
  const attached = (point: PlanPoint): ResolvedCommentPin =>
    Number.isFinite(point.x) && Number.isFinite(point.y) ? { point, storey: pin.storey, state: "attached" } : detached;

  switch (target.kind) {
    case "wall":
    case "opening": {
      const segment = findSegment(rooms, target.id);
      if (!segment) return detached;
      if (!anchor || anchor.frame !== "segment") return { ...dropped, state: "attached" };
      return attached(onSegment(segment.start, segment.end, anchor.t, anchor.offsetM));
    }
    case "room": {
      const room = findRoomByFloor(rooms, target.id);
      if (!room) return detached;
      if (!anchor || anchor.frame !== "local") return { ...dropped, state: "attached" };
      return attached({ x: room.centre.x + anchor.u, y: room.centre.y + anchor.v });
    }
    case "object": {
      const object = objects.find((o) => o.id === target.id);
      if (!object) return detached;
      const g = object.geometry;
      if (!anchor) return { ...dropped, state: "attached" };
      if (isRun(g)) {
        if (anchor.frame !== "segment") return { ...dropped, state: "attached" };
        const leg = Math.min(Math.max(anchor.leg ?? 0, 0), g.points.length - 2);
        const a = g.points[leg];
        const b = g.points[leg + 1];
        if (!a || !b) return detached;
        return attached(onSegment(a, b, anchor.t, anchor.offsetM));
      }
      if (anchor.frame !== "local") return { ...dropped, state: "attached" };
      return attached(fromLocal({ x: g.x, y: g.y }, g.rotationDeg, anchor.u, anchor.v));
    }
  }
}

/** A pin standing this far above its room's floor in the 3D view, when nothing better is known. */
export const COMMENT_PIN_HEIGHT_M = 1.2;

/**
 * Where a pin stands in the 3D model's world (Y up): its plan point as world X
 * and Z, and a height — the one it was dropped at on the model; else half way
 * up the wall or opening it is attached to; else 1.2 m above the floor of the
 * room it is in, or of the storey. Null when no height can be found at all
 * (no floor anywhere on that storey was settled).
 */
export function commentPinWorldPoint(
  pin: CommentPin,
  resolved: ResolvedCommentPin,
  rooms: readonly PlanRoom[],
): { readonly x: number; readonly y: number; readonly z: number } | null {
  const at = (y: number) => ({ x: resolved.point.x, y, z: resolved.point.y });
  if (typeof pin.heightM === "number" && Number.isFinite(pin.heightM) && resolved.state !== "detached") {
    return at(pin.heightM);
  }
  if (pin.target && (pin.target.kind === "wall" || pin.target.kind === "opening")) {
    const vertical = findSegment(rooms, pin.target.id)?.vertical;
    if (vertical) return at((vertical.bottomY + vertical.topY) / 2);
  }
  const storeyRooms = rooms.filter((room) => room.story === pin.storey);
  const containing = storeyRooms.find((room) => room.outlines.some((o) => pointInPolygon(resolved.point, o)));
  if (containing?.floorLevel.known) return at(containing.floorLevel.y + COMMENT_PIN_HEIGHT_M);
  const levels = storeyRooms.flatMap((room) => (room.floorLevel.known ? [room.floorLevel.y] : []));
  if (levels.length === 0) return null;
  const sorted = [...levels].sort((a, b) => a - b);
  return at((sorted[Math.floor(sorted.length / 2)] as number) + COMMENT_PIN_HEIGHT_M);
}
